import { FunctionalComponent, h, VNode } from '@stencil/core';
import type { TextFieldTypes } from '../../interface';

interface FormControlWrapProps {
  rtl: boolean;
  floatLabel: boolean;
  labelId: string;
  label: string;
  moreId: string;
  helperEndId: string;
  type?: TextFieldTypes;
  showInlineError: boolean;
  hasHelperSlot: boolean;
  errorMessage: string;
  controlId: string;
  hasLabelSlot: boolean;
  hideLabel: boolean;
  placeholder: string;
  hasValue: boolean;
  showCharCount?: boolean;
  maxlength?: number;
  charCount?: number;
  hasHelperEndSlot?: boolean;
}

interface FormControlProps {
  clearControl: boolean;
  onClearText?: () => void;
  readonly: boolean;
  disabled: boolean;
  control: HTMLNanoInputElement | HTMLNanoSelectElement;
  ref?: (elm?: HTMLElement) => void;
  endSlot?: VNode;
  endValueSlot?: VNode;
  startSlot?: VNode;
}

interface LabelProps extends FormControlWrapProps {
  rtl: boolean;
}

const renderLabel = ({
  label,
  hasLabelSlot,
  controlId,
  labelId,
  floatLabel,
  placeholder,
  hideLabel,
  hasValue,
  rtl,
  showCharCount,
  maxlength,
  charCount,
}: LabelProps) => {
  if (!label && !hasLabelSlot) return;
  if (floatLabel) {
    return (
      <label class="form-ctrl__float-label" htmlFor={controlId} id={labelId}>
        {label && label}
        {!label && hasLabelSlot && <slot name="label" />}
        {showCharCount && (
          <span class="form-ctrl__label-charcount">
            {maxlength ? ` ${charCount}/${maxlength}` : ` ${charCount}`}
          </span>
        )}
      </label>
    );
  } else {
    return (
      <label
        class={{ 'form-ctrl__label': true, 'visually-hide': hideLabel }}
        htmlFor={controlId}
        id={labelId}
      >
        <div class="form-ctrl__label-wrap">
          {placeholder && hasValue && rtl && (
            <span class="form-ctrl__label-placeholder">{placeholder} / </span>
          )}
          <span class="form-ctrl__labeltext">
            {label && label}
            {!label && hasLabelSlot && <slot name="label" />}
          </span>
          {placeholder && hasValue && !rtl && (
            <span class="form-ctrl__label-placeholder"> / {placeholder}</span>
          )}
        </div>
        {showCharCount && (
          <span class="form-ctrl__label-charcount">
            {maxlength
              ? maxlength - charCount + ' left'
              : charCount + ' characters'}
          </span>
        )}
      </label>
    );
  }
};

export const FormControlWrap: FunctionalComponent<FormControlWrapProps> = (
  props: FormControlWrapProps,
  children
) => {
  const {
    rtl,
    floatLabel,
    label,
    moreId,
    helperEndId,
    type,
    hasHelperSlot,
    showInlineError,
    errorMessage,
    hasHelperEndSlot,
  } = props;

  const MainTag = hasHelperEndSlot ? 'nano-resize-observe' : 'div';

  return (
    <MainTag
      states="350w has-enough-width"
      class={{
        'has-label': label !== null && !floatLabel,
        'has-float-label': label !== null && floatLabel,
        'has-helper-end': hasHelperEndSlot,
        rtl,
        'form-ctrl': true,
      }}
    >
      <div class="form-ctrl__wrapper">
        {!floatLabel ? renderLabel({ ...props }) : ''}
        <div
          class={{
            'form-ctrl__input': true,
            'form-ctrl__textarea': type === 'textarea',
          }}
        >
          {floatLabel && renderLabel({ ...props, rtl })}
          {children}
        </div>
        {showInlineError || hasHelperSlot ? (
          <div class="form-ctrl__more" id={moreId}>
            {!!showInlineError ? (
              <div class="form-ctrl__error" aria-live="polite">
                {errorMessage}
              </div>
            ) : (
              ''
            )}
            <div class="form-ctrl__helper">
              <slot name="helper" />
            </div>
          </div>
        ) : (
          ''
        )}
      </div>
      {hasHelperEndSlot ? (
        <div class="form-ctrl__helper-end" id={helperEndId}>
          <slot name="helper-end" />
        </div>
      ) : (
        ''
      )}
    </MainTag>
  );
};

export const FormControl: FunctionalComponent<FormControlProps> = (
  props: FormControlProps,
  children
) => {
  const {
    clearControl,
    onClearText,
    readonly,
    disabled,
    control,
    ref,
    endSlot,
    endValueSlot,
    startSlot,
  } = props;
  const clickThrough = () => {
    control.setFocus();
  };

  return (
    <div
      class="form-ctrl__input-wrap"
      ref={(el) => {
        if (ref) ref(el);
      }}
    >
      <span class="form-ctrl__slot-start">
        <slot name="start" />
        {startSlot}
      </span>
      {children}
      {clearControl && !readonly && !disabled && (
        <button
          type="button"
          class="icon form-ctrl__clear-btn"
          tabindex="-1"
          onTouchStart={onClearText}
          onMouseDown={onClearText}
        >
          <nano-icon name="light/times"></nano-icon>
        </button>
      )}
      <span class="form-ctrl__slot-value-end" onClick={clickThrough}>
        <slot name="value-end" />
        {endValueSlot}
      </span>
      <span class="form-ctrl__slot-end">
        <slot name="end" />
        {endSlot}
      </span>
    </div>
  );
};
